// Orquestrador › Agendamentos: rotinas que rodam sozinhas, por módulo.
// Tudo nasce desligado (chave geral + cada rotina + cada empresa em "Manual").
import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CalendarCheck,
  ClockCounterClockwise,
  Eye,
  FloppyDisk,
  MagnifyingGlass,
  Play,
  Warning,
} from 'phosphor-react';
import { GlassPageBg } from '../../../components/glass/GlassPageBg';
import { GlassCard } from '../../../components/glass/GlassCard';
import {
  Automacao,
  IEmpresaAutomacao,
  IExecucao,
  IRotina,
  ISimulacao,
  Modulo,
  ROTINAS_ESCOPO_TODAS,
  ROTULO_AUTOMACAO,
  StatusExecucao,
  agApi,
  mensagemErro,
} from './api';

const MODULO: Modulo = 'simples_nacional';

const dataHora = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const mesAno = (iso?: string | null) => {
  if (!iso) return '—';
  const [a, m] = iso.split('-');
  return `${m}/${a}`;
};

const COR_STATUS: Record<StatusExecucao, string> = {
  rodando: 'blue',
  concluida: 'green',
  falhou: 'red',
  interrompida: 'orange',
};

const ROTULO_STATUS: Record<StatusExecucao, string> = {
  rodando: 'Rodando',
  concluida: 'Concluída',
  falhou: 'Falhou',
  interrompida: 'Interrompida',
};

function StatusExecucaoBadge({ ex }: { ex: IExecucao }) {
  return (
    <Badge colorScheme={COR_STATUS[ex.status]} fontSize="10px">
      {ROTULO_STATUS[ex.status]}
    </Badge>
  );
}

// --- confirmação ------------------------------------------------------------------

function ConfirmarModal({
  titulo,
  children,
  palavra,
  carregando,
  onConfirmar,
  onFechar,
}: {
  titulo: string;
  children: React.ReactNode;
  /** Se informada, o usuário precisa digitar esta palavra para confirmar. */
  palavra?: string;
  carregando?: boolean;
  onConfirmar: () => void;
  onFechar: () => void;
}) {
  const [texto, setTexto] = useState('');
  const liberado = !palavra || texto.trim().toUpperCase() === palavra;
  return (
    <Modal isOpen onClose={onFechar} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">{titulo}</ModalHeader>
        <ModalCloseButton />
        <ModalBody fontSize="13px">
          {children}
          {palavra && (
            <Box mt={3}>
              <Text fontSize="12px" color="gray.600" mb={1}>
                Digite <b>{palavra}</b> para confirmar:
              </Text>
              <Input size="sm" value={texto} onChange={e => setTexto(e.target.value)} />
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            isDisabled={!liberado}
            isLoading={carregando}
            onClick={onConfirmar}
          >
            Confirmar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// --- simulação --------------------------------------------------------------------

function SimulacaoModal({
  rotina,
  dados,
  onFechar,
}: {
  rotina: IRotina;
  dados: ISimulacao[];
  onFechar: () => void;
}) {
  return (
    <Modal isOpen onClose={onFechar} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">Simular: {rotina.nome}</ModalHeader>
        <ModalCloseButton />
        <ModalBody fontSize="13px">
          <Text color="gray.600" mb={3}>
            O que a rotina faria se rodasse agora. Nada foi executado.
          </Text>
          {dados.map(s => (
            <Box key={s.competencia} mb={4}>
              <Text fontWeight={700} mb={2}>
                Competência {mesAno(s.competencia)}: {s.alvos.length} roda(m), {s.puladas.length}{' '}
                fica(m) de fora
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                <Box>
                  <Text fontSize="12px" fontWeight={600} color="green.700" mb={1}>
                    Vão rodar
                  </Text>
                  <Box maxH="260px" overflowY="auto" borderWidth="1px" borderRadius="md" p={2}>
                    {s.alvos.length === 0 && <Text color="gray.500">Nenhuma</Text>}
                    {s.alvos.map(a => (
                      <Text key={a.empresa_id} fontSize="12px">
                        {a.nome}
                      </Text>
                    ))}
                  </Box>
                </Box>
                <Box>
                  <Text fontSize="12px" fontWeight={600} color="orange.700" mb={1}>
                    Ficam de fora (motivo)
                  </Text>
                  <Box maxH="260px" overflowY="auto" borderWidth="1px" borderRadius="md" p={2}>
                    {s.puladas.length === 0 && <Text color="gray.500">Nenhuma</Text>}
                    {s.puladas.map(p => (
                      <Text key={p.empresa_id} fontSize="12px">
                        {p.nome} — <i>{p.motivo}</i>
                      </Text>
                    ))}
                  </Box>
                </Box>
              </SimpleGrid>
            </Box>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={onFechar}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// --- detalhe da execução ----------------------------------------------------------

function ExecucaoModal({ id, onFechar }: { id: number; onFechar: () => void }) {
  const { data: ex, isLoading } = useQuery({
    queryKey: ['ag-execucao', id],
    queryFn: () => agApi.execucao(id),
    refetchInterval: q => (q.state.data?.status === 'rodando' ? 5000 : false),
  });
  return (
    <Modal isOpen onClose={onFechar} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">Execução #{id}</ModalHeader>
        <ModalCloseButton />
        <ModalBody fontSize="13px">
          {isLoading || !ex ? (
            <Spinner />
          ) : (
            <>
              <Flex gap={3} wrap="wrap" mb={3} align="center">
                <StatusExecucaoBadge ex={ex} />
                <Text>Competência {mesAno(ex.competencia)}</Text>
                <Text>
                  {ex.disparo === 'agenda' ? 'Pela agenda' : `Manual (${ex.usuario_nome ?? '—'})`}
                </Text>
                <Text>
                  {dataHora(ex.iniciado_em)} → {dataHora(ex.finalizado_em)}
                </Text>
              </Flex>
              <Text mb={3}>
                {ex.ok} ok · {ex.falhas} com erro · {ex.puladas} de fora · {ex.total} no total
              </Text>
              {ex.erro && (
                <Text color="red.600" mb={3}>
                  {ex.erro}
                </Text>
              )}
              {(ex.resultados ?? []).some(r => !r.ok) && (
                <Box mb={3}>
                  <Text fontWeight={700} color="red.700" mb={1}>
                    Com erro
                  </Text>
                  {(ex.resultados ?? [])
                    .filter(r => !r.ok)
                    .map(r => (
                      <Text key={`${r.empresa_id}-${r.competencia}`} fontSize="12px">
                        {r.nome ?? r.empresa_id} ({mesAno(r.competencia)}) — {r.erro ?? 'erro'}
                      </Text>
                    ))}
                </Box>
              )}
              {(ex.puladas_detalhe ?? []).length > 0 && (
                <Box mb={3}>
                  <Text fontWeight={700} color="orange.700" mb={1}>
                    Ficaram de fora
                  </Text>
                  {(ex.puladas_detalhe ?? []).map(p => (
                    <Text key={`${p.empresa_id}-${p.competencia}`} fontSize="12px">
                      {p.nome} — <i>{p.motivo}</i>
                    </Text>
                  ))}
                </Box>
              )}
              {(ex.resultados ?? []).some(r => r.ok) && (
                <Box>
                  <Text fontWeight={700} color="green.700" mb={1}>
                    Ok
                  </Text>
                  {(ex.resultados ?? [])
                    .filter(r => r.ok)
                    .map(r => (
                      <Text key={`${r.empresa_id}-${r.competencia}`} fontSize="12px">
                        {r.nome ?? r.empresa_id} ({mesAno(r.competencia)})
                      </Text>
                    ))}
                </Box>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={onFechar}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// --- uma rotina -------------------------------------------------------------------

function RotinaCard({
  rotina,
  podeConfigurar,
  podeTransmitir,
  moduloLigado,
  onVerExecucao,
}: {
  rotina: IRotina;
  podeConfigurar: boolean;
  podeTransmitir: boolean;
  moduloLigado: boolean;
  onVerExecucao: (id: number) => void;
}) {
  const qc = useQueryClient();
  const [ativo, setAtivo] = useState(rotina.ativo);
  const [dias, setDias] = useState(rotina.dias);
  const [hora, setHora] = useState(rotina.hora);
  const [escopo, setEscopo] = useState(rotina.opcoes?.escopo ?? 'automacao');
  const [simulacao, setSimulacao] = useState<ISimulacao[] | null>(null);
  const [confirmarRodar, setConfirmarRodar] = useState(false);
  const [confirmarAtivar, setConfirmarAtivar] = useState(false);

  const aceitaEscopo = ROTINAS_ESCOPO_TODAS.includes(rotina.rotina);
  const pode = podeConfigurar && (!rotina.transmite || podeTransmitir);
  const mudou =
    ativo !== rotina.ativo ||
    dias !== rotina.dias ||
    hora !== rotina.hora ||
    (aceitaEscopo && escopo !== (rotina.opcoes?.escopo ?? 'automacao'));

  const atualizar = () => {
    qc.invalidateQueries({ queryKey: ['ag-modulo', MODULO] });
    qc.invalidateQueries({ queryKey: ['ag-execucoes', MODULO] });
  };

  const salvar = useMutation({
    mutationFn: () =>
      agApi.salvarRotina(rotina.id, {
        ativo,
        dias,
        hora,
        ...(aceitaEscopo ? { opcoes: { ...rotina.opcoes, escopo } } : {}),
      }),
    onSuccess: () => {
      toast.success(`${rotina.nome}: salvo.`);
      setConfirmarAtivar(false);
      atualizar();
    },
    onError: e => toast.error(mensagemErro(e)),
  });

  const simular = useMutation({
    mutationFn: () => agApi.simular(rotina.id),
    onSuccess: setSimulacao,
    onError: e => toast.error(mensagemErro(e)),
  });

  const rodar = useMutation({
    mutationFn: () => agApi.executar(rotina.id),
    onSuccess: () => {
      toast.success(`${rotina.nome}: rodando. Acompanhe no histórico.`);
      setConfirmarRodar(false);
      setTimeout(atualizar, 1500);
    },
    onError: e => toast.error(mensagemErro(e)),
  });

  const pedirSalvar = () => {
    // ligar uma rotina que mexe na Receita pede confirmação
    if (ativo && !rotina.ativo && rotina.transmite) setConfirmarAtivar(true);
    else salvar.mutate();
  };

  return (
    <GlassCard intensity="strong" p="14px 16px">
      <Flex gap={3} align="flex-start" wrap="wrap">
        <Switch
          mt="2px"
          colorScheme="green"
          isChecked={ativo}
          isDisabled={!pode}
          onChange={e => setAtivo(e.target.checked)}
          aria-label={`Ativar ${rotina.nome}`}
        />
        <Box flex={1} minW="220px">
          <Flex align="center" gap={2} wrap="wrap">
            <Text fontWeight={700} fontSize="14px" color="#0F172A">
              {rotina.nome}
            </Text>
            {rotina.transmite && (
              <Badge colorScheme="red" fontSize="9px">
                Mexe na Receita
              </Badge>
            )}
            {rotina.usa_serpro && !rotina.transmite && (
              <Badge colorScheme="purple" fontSize="9px">
                Consulta SERPRO
              </Badge>
            )}
          </Flex>
          <Text fontSize="12px" color="#64748B" mt={1}>
            {rotina.descricao}
          </Text>
        </Box>
      </Flex>

      <Flex gap={3} mt={3} wrap="wrap" align="flex-end">
        <Box>
          <Text fontSize="11px" color="#64748B" mb={1}>
            Dias do mês
          </Text>
          <Tooltip
            label="Ex.: 1-31 (todo dia), 10-18, 5,10,15. Dia que o mês não tem vira o último."
            fontSize="11px"
            hasArrow
          >
            <Input
              size="sm"
              w="140px"
              value={dias}
              isDisabled={!pode}
              onChange={e => setDias(e.target.value)}
            />
          </Tooltip>
        </Box>
        <Box>
          <Text fontSize="11px" color="#64748B" mb={1}>
            Hora
          </Text>
          <Input
            size="sm"
            w="110px"
            type="time"
            value={hora}
            isDisabled={!pode}
            onChange={e => setHora(e.target.value)}
          />
        </Box>
        {aceitaEscopo && (
          <Box>
            <Text fontSize="11px" color="#64748B" mb={1}>
              Empresas
            </Text>
            <Select
              size="sm"
              w="230px"
              value={escopo}
              isDisabled={!pode}
              onChange={e => setEscopo(e.target.value as 'todas' | 'automacao')}
            >
              <option value="todas">Todas do Simples</option>
              <option value="automacao">Só as marcadas (calcular/transmitir)</option>
            </Select>
          </Box>
        )}
        <Button
          size="sm"
          colorScheme="blue"
          leftIcon={<FloppyDisk size={14} />}
          isDisabled={!pode || !mudou}
          isLoading={salvar.isPending}
          onClick={pedirSalvar}
        >
          Salvar
        </Button>
        <Box flex={1} />
        <Button
          size="sm"
          variant="outline"
          leftIcon={<Eye size={14} />}
          isLoading={simular.isPending}
          onClick={() => simular.mutate()}
        >
          Simular
        </Button>
        <Button
          size="sm"
          variant="outline"
          colorScheme={rotina.transmite ? 'red' : 'gray'}
          leftIcon={<Play size={14} />}
          isDisabled={!pode}
          onClick={() => setConfirmarRodar(true)}
        >
          Rodar agora
        </Button>
      </Flex>

      <Flex gap={4} mt={3} wrap="wrap" fontSize="12px" color="#475569">
        <Text>
          Próxima:{' '}
          <b>
            {rotina.proxima && moduloLigado ? dataHora(rotina.proxima) : 'não agendada (desligada)'}
          </b>
        </Text>
        {rotina.ultima && (
          <Flex gap={2} align="center">
            <Text>Última: {dataHora(rotina.ultima.iniciado_em)}</Text>
            <StatusExecucaoBadge ex={rotina.ultima} />
            <Text>
              {rotina.ultima.ok} ok · {rotina.ultima.falhas} erro · {rotina.ultima.puladas} de fora
            </Text>
            <Button size="xs" variant="link" onClick={() => onVerExecucao(rotina.ultima!.id)}>
              detalhes
            </Button>
          </Flex>
        )}
        {rotina.atualizado_por_nome && (
          <Text color="#94A3B8">
            Alterado por {rotina.atualizado_por_nome} em {dataHora(rotina.dt_atualizacao)}
          </Text>
        )}
      </Flex>

      {simulacao && (
        <SimulacaoModal rotina={rotina} dados={simulacao} onFechar={() => setSimulacao(null)} />
      )}
      {confirmarRodar && (
        <ConfirmarModal
          titulo={`Rodar agora: ${rotina.nome}`}
          palavra={rotina.transmite ? 'TRANSMITIR' : undefined}
          carregando={rodar.isPending}
          onConfirmar={() => rodar.mutate()}
          onFechar={() => setConfirmarRodar(false)}
        >
          <Text>
            Roda agora, mesmo com a chave geral desligada, nas empresas que a rotina escolhe (use{' '}
            <b>Simular</b> antes para ver quais).
          </Text>
          {rotina.transmite && (
            <Text mt={2} color="red.600">
              Esta rotina envia para a Receita Federal.
            </Text>
          )}
        </ConfirmarModal>
      )}
      {confirmarAtivar && (
        <ConfirmarModal
          titulo={`Ligar: ${rotina.nome}`}
          palavra="TRANSMITIR"
          carregando={salvar.isPending}
          onConfirmar={() => salvar.mutate()}
          onFechar={() => setConfirmarAtivar(false)}
        >
          <Text>
            Com a chave geral ligada, esta rotina vai mexer na Receita sozinha nos dias{' '}
            <b>{dias}</b> às <b>{hora}</b>, só nas empresas em <b>Calcular e transmitir</b> que
            passarem nas travas.
          </Text>
        </ConfirmarModal>
      )}
    </GlassCard>
  );
}

// --- empresas ---------------------------------------------------------------------

function EmpresasSecao({
  podeConfigurar,
  podeTransmitir,
}: {
  podeConfigurar: boolean;
  podeTransmitir: boolean;
}) {
  const qc = useQueryClient();
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<'todas' | Automacao>('todas');
  const [marcadas, setMarcadas] = useState<Set<number>>(new Set());
  const [emMassa, setEmMassa] = useState<Automacao>('calcular');
  const [confirmar, setConfirmar] = useState<{ ids: number[]; automacao: Automacao } | null>(null);

  const { data = [], isLoading } = useQuery({
    queryKey: ['ag-empresas', MODULO],
    queryFn: agApi.empresas,
  });

  const lista = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return data.filter(
      e =>
        (filtro === 'todas' || e.automacao === filtro) &&
        (!t || e.nome.toLowerCase().includes(t) || String(e.external_id ?? '').includes(t)),
    );
  }, [data, busca, filtro]);

  const salvar = useMutation({
    mutationFn: (v: { ids: number[]; automacao: Automacao }) =>
      agApi.salvarEmpresas(v.ids, v.automacao),
    onSuccess: r => {
      toast.success(`${r.alteradas} empresa(s) alterada(s).`);
      setConfirmar(null);
      setMarcadas(new Set());
      qc.invalidateQueries({ queryKey: ['ag-empresas', MODULO] });
      qc.invalidateQueries({ queryKey: ['ag-modulo', MODULO] });
    },
    onError: e => toast.error(mensagemErro(e)),
  });

  const pedir = (ids: number[], automacao: Automacao) => {
    if (automacao === 'transmitir') setConfirmar({ ids, automacao });
    else salvar.mutate({ ids, automacao });
  };

  const alternar = (id: number) =>
    setMarcadas(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const todasMarcadas = lista.length > 0 && lista.every(e => marcadas.has(e.empresa_id));

  const contagem = (a: Automacao) => data.filter(e => e.automacao === a).length;

  return (
    <GlassCard intensity="strong" p="14px 16px">
      <Text fontWeight={700} fontSize="14px" color="#0F172A">
        Empresas
      </Text>
      <Text fontSize="12px" color="#64748B" mb={3}>
        Cada empresa nasce em <b>Manual</b>. <b>Só calcular</b>: entra no recálculo e na busca do
        RBT12. <b>Calcular e transmitir</b>: também é aprovada e transmitida sozinha, se passar nas
        travas (com RBT12, sem pendência, sem ressalva e fatura do mês lançada). Empresa com fatura
        a lançar fica de fora até alguém lançar.
      </Text>
      <Flex gap={2} wrap="wrap" mb={3}>
        {(['manual', 'calcular', 'transmitir'] as Automacao[]).map(a => (
          <Badge key={a} fontSize="11px" colorScheme={a === 'transmitir' ? 'red' : 'gray'}>
            {ROTULO_AUTOMACAO[a]}: {contagem(a)}
          </Badge>
        ))}
      </Flex>
      <Flex gap={2} wrap="wrap" mb={3} align="center">
        <Flex align="center" gap={2} flex={1} minW="220px">
          <MagnifyingGlass size={16} color="#64748B" />
          <Input
            size="sm"
            placeholder="Buscar por nome ou código"
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
        </Flex>
        <Select
          size="sm"
          w="200px"
          value={filtro}
          onChange={e => setFiltro(e.target.value as 'todas' | Automacao)}
        >
          <option value="todas">Todas</option>
          <option value="manual">Manual</option>
          <option value="calcular">Só calcular</option>
          <option value="transmitir">Calcular e transmitir</option>
        </Select>
        {podeConfigurar && marcadas.size > 0 && (
          <Flex gap={2} align="center">
            <Text fontSize="12px">{marcadas.size} marcada(s) →</Text>
            <Select
              size="sm"
              w="190px"
              value={emMassa}
              onChange={e => setEmMassa(e.target.value as Automacao)}
            >
              <option value="manual">Manual</option>
              <option value="calcular">Só calcular</option>
              <option value="transmitir" disabled={!podeTransmitir}>
                Calcular e transmitir
              </option>
            </Select>
            <Button
              size="sm"
              colorScheme="blue"
              isLoading={salvar.isPending}
              onClick={() => pedir(Array.from(marcadas), emMassa)}
            >
              Aplicar
            </Button>
          </Flex>
        )}
      </Flex>

      {isLoading ? (
        <Spinner />
      ) : (
        <Box maxH="520px" overflowY="auto" borderWidth="1px" borderRadius="md">
          <Flex
            px={3}
            py={2}
            gap={3}
            align="center"
            bg="gray.50"
            fontSize="11px"
            fontWeight={700}
            color="#475569"
            position="sticky"
            top={0}
            zIndex={1}
          >
            <Checkbox
              isChecked={todasMarcadas}
              isDisabled={!podeConfigurar}
              onChange={e =>
                setMarcadas(e.target.checked ? new Set(lista.map(x => x.empresa_id)) : new Set())
              }
            />
            <Text flex={1}>Empresa ({lista.length})</Text>
            <Text w="240px" display={{ base: 'none', md: 'block' }}>
              Trava hoje
            </Text>
            <Text w="200px">Automação</Text>
          </Flex>
          {lista.map(e => (
            <LinhaEmpresa
              key={e.empresa_id}
              e={e}
              marcada={marcadas.has(e.empresa_id)}
              podeConfigurar={podeConfigurar}
              podeTransmitir={podeTransmitir}
              onMarcar={() => alternar(e.empresa_id)}
              onMudar={a => pedir([e.empresa_id], a)}
            />
          ))}
        </Box>
      )}

      {confirmar && (
        <ConfirmarModal
          titulo="Calcular e transmitir"
          palavra="TRANSMITIR"
          carregando={salvar.isPending}
          onConfirmar={() => salvar.mutate(confirmar)}
          onFechar={() => setConfirmar(null)}
        >
          <Text>
            {confirmar.ids.length} empresa(s) poderão ser aprovadas e transmitidas para a Receita
            pelas rotinas ligadas, sempre respeitando as travas.
          </Text>
        </ConfirmarModal>
      )}
    </GlassCard>
  );
}

function LinhaEmpresa({
  e,
  marcada,
  podeConfigurar,
  podeTransmitir,
  onMarcar,
  onMudar,
}: {
  e: IEmpresaAutomacao;
  marcada: boolean;
  podeConfigurar: boolean;
  podeTransmitir: boolean;
  onMarcar: () => void;
  onMudar: (a: Automacao) => void;
}) {
  return (
    <Flex px={3} py={2} gap={3} align="center" borderTopWidth="1px" fontSize="12px">
      <Checkbox isChecked={marcada} isDisabled={!podeConfigurar} onChange={onMarcar} />
      <Box flex={1} minW={0}>
        <Text noOfLines={1} fontWeight={600} color="#0F172A">
          {e.external_id ? `${e.external_id} · ` : ''}
          {e.nome}
        </Text>
        {e.fiscal && <Text color="#94A3B8">{e.fiscal}</Text>}
      </Box>
      <Flex w="240px" display={{ base: 'none', md: 'flex' }} align="center" gap={1}>
        {e.trava ? (
          <>
            <Warning size={14} color="#D97706" />
            <Text color="#B45309" noOfLines={2}>
              {e.trava}
            </Text>
          </>
        ) : (
          <Text color="#15803D">Livre</Text>
        )}
      </Flex>
      <Select
        size="xs"
        w="200px"
        value={e.automacao}
        isDisabled={!podeConfigurar || (e.automacao === 'transmitir' && !podeTransmitir)}
        onChange={ev => onMudar(ev.target.value as Automacao)}
      >
        <option value="manual">Manual</option>
        <option value="calcular">Só calcular</option>
        <option value="transmitir" disabled={!podeTransmitir}>
          Calcular e transmitir
        </option>
      </Select>
    </Flex>
  );
}

// --- histórico --------------------------------------------------------------------

function HistoricoSecao({
  nomes,
  onVer,
}: {
  nomes: Record<string, string>;
  onVer: (id: number) => void;
}) {
  const { data = [], isLoading } = useQuery({
    queryKey: ['ag-execucoes', MODULO],
    queryFn: () => agApi.execucoes(MODULO),
    refetchInterval: q => (q.state.data?.some(x => x.status === 'rodando') ? 5000 : false),
  });
  return (
    <GlassCard intensity="strong" p="14px 16px">
      <Flex align="center" gap={2} mb={3}>
        <ClockCounterClockwise size={18} />
        <Text fontWeight={700} fontSize="14px" color="#0F172A">
          Histórico
        </Text>
      </Flex>
      {isLoading && <Spinner />}
      {!isLoading && data.length === 0 && (
        <Text fontSize="12px" color="#64748B">
          Nenhuma execução ainda.
        </Text>
      )}
      {data.map(ex => (
        <Flex
          key={ex.id}
          gap={3}
          py={2}
          borderTopWidth="1px"
          align="center"
          fontSize="12px"
          wrap="wrap"
        >
          <Text w="90px">{dataHora(ex.iniciado_em)}</Text>
          <Text flex={1} minW="160px" fontWeight={600}>
            {nomes[ex.rotina] ?? ex.rotina}
          </Text>
          <Text w="70px">{mesAno(ex.competencia)}</Text>
          <Text w="150px" color="#64748B" noOfLines={1}>
            {ex.disparo === 'agenda' ? 'Agenda' : `Manual · ${ex.usuario_nome ?? ''}`}
          </Text>
          <StatusExecucaoBadge ex={ex} />
          <Text w="150px">
            {ex.ok}/{ex.total} ok{ex.falhas ? ` · ${ex.falhas} erro` : ''}
            {ex.puladas ? ` · ${ex.puladas} fora` : ''}
          </Text>
          <Button size="xs" variant="link" onClick={() => onVer(ex.id)}>
            detalhes
          </Button>
        </Flex>
      ))}
    </GlassCard>
  );
}

// --- aba Simples Nacional ---------------------------------------------------------

function SimplesNacionalAba() {
  const qc = useQueryClient();
  const [execucao, setExecucao] = useState<number | null>(null);
  const [confirmarChave, setConfirmarChave] = useState<boolean | null>(null);
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['ag-modulo', MODULO],
    queryFn: () => agApi.modulo(MODULO),
    refetchInterval: q =>
      q.state.data?.rotinas.some(r => r.ultima?.status === 'rodando') ? 5000 : false,
  });

  const chave = useMutation({
    mutationFn: (ligado: boolean) => agApi.salvarChave(MODULO, ligado),
    onSuccess: r => {
      toast.success(r.ligado ? 'Agendamentos do Simples ligados.' : 'Agendamentos desligados.');
      setConfirmarChave(null);
      qc.invalidateQueries({ queryKey: ['ag-modulo', MODULO] });
      qc.invalidateQueries({ queryKey: ['sn-config'] });
    },
    onError: e => toast.error(mensagemErro(e)),
  });

  if (isError) {
    return (
      <GlassCard intensity="strong" p="14px 16px">
        <Flex align="center" gap={2} color="red.600" mb={2}>
          <Warning size={18} />
          <Text fontWeight={700} fontSize="14px">
            Não deu para carregar os agendamentos
          </Text>
        </Flex>
        <Text fontSize="12px" color="#475569" mb={3}>
          {mensagemErro(error)}
        </Text>
        <Button size="sm" onClick={() => refetch()} isLoading={isFetching}>
          Tentar de novo
        </Button>
      </GlassCard>
    );
  }
  if (isLoading || !data) return <Spinner />;

  const nomes = Object.fromEntries(data.rotinas.map(r => [r.rotina, r.nome]));
  const ativas = data.rotinas.filter(r => r.ativo);

  return (
    <Flex direction="column" gap={4}>
      <GlassCard
        intensity="strong"
        p="14px 16px"
        borderColor={data.ligado ? 'green.300' : undefined}
      >
        <Flex gap={3} align="center" wrap="wrap">
          <Switch
            size="lg"
            colorScheme="green"
            isChecked={data.ligado}
            isDisabled={!data.pode_configurar || chave.isPending}
            onChange={e => setConfirmarChave(e.target.checked)}
            aria-label="Chave geral do Simples"
          />
          <Box flex={1} minW="220px">
            <Text fontWeight={700} fontSize="15px" color="#0F172A">
              Chave geral: {data.ligado ? 'ligada' : 'desligada'}
            </Text>
            <Text fontSize="12px" color="#64748B">
              Desligada, nenhuma rotina roda sozinha (o &quot;Rodar agora&quot; continua
              funcionando). Ligada, roda só as rotinas ativas abaixo, nos dias e horários definidos
              (horário de Brasília). Competência: sempre o mês anterior.
              {data.atualizado_por_nome &&
                ` Alterada por ${data.atualizado_por_nome} em ${dataHora(data.dt_atualizacao)}.`}
            </Text>
          </Box>
          <Flex gap={2} wrap="wrap">
            <Badge fontSize="11px">
              {ativas.length} de {data.rotinas.length} rotina(s) ativa(s)
            </Badge>
            <Badge fontSize="11px">{data.automacao.calcular} em Só calcular</Badge>
            <Badge fontSize="11px" colorScheme="red">
              {data.automacao.transmitir} em Calcular e transmitir
            </Badge>
          </Flex>
        </Flex>
      </GlassCard>

      {data.rotinas.map(r => (
        <RotinaCard
          key={`${r.id}-${r.dt_atualizacao}`}
          rotina={r}
          podeConfigurar={data.pode_configurar}
          podeTransmitir={data.pode_transmitir}
          moduloLigado={data.ligado}
          onVerExecucao={setExecucao}
        />
      ))}

      <EmpresasSecao podeConfigurar={data.pode_configurar} podeTransmitir={data.pode_transmitir} />
      <HistoricoSecao nomes={nomes} onVer={setExecucao} />

      {execucao !== null && <ExecucaoModal id={execucao} onFechar={() => setExecucao(null)} />}
      {confirmarChave !== null && (
        <ConfirmarModal
          titulo={confirmarChave ? 'Ligar agendamentos do Simples' : 'Desligar agendamentos'}
          palavra={confirmarChave ? 'LIGAR' : undefined}
          carregando={chave.isPending}
          onConfirmar={() => chave.mutate(confirmarChave)}
          onFechar={() => setConfirmarChave(null)}
        >
          {confirmarChave ? (
            <>
              <Text>Vão passar a rodar sozinhas:</Text>
              {ativas.length === 0 && <Text color="gray.500">nenhuma rotina ativa ainda</Text>}
              {ativas.map(r => (
                <Text key={r.id} fontSize="12px">
                  • {r.nome}: dias {r.dias} às {r.hora}
                </Text>
              ))}
            </>
          ) : (
            <Text>Nada mais roda sozinho até ligar de novo.</Text>
          )}
        </ConfirmarModal>
      )}
    </Flex>
  );
}

export function AgendamentosPage() {
  return (
    <GlassPageBg>
      <Flex direction="column" gap={5} px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        <Flex align="center" gap={3}>
          <Flex
            w="46px"
            h="46px"
            borderRadius="14px"
            align="center"
            justify="center"
            flex="none"
            bgGradient="linear(135deg, #1B88F5, #6366F1)"
            color="white"
          >
            <CalendarCheck size={24} weight="duotone" />
          </Flex>
          <Box>
            <Text fontSize="20px" fontWeight={800} color="#0F172A">
              Agendamentos
            </Text>
            <Text fontSize="13px" color="#64748B">
              O que roda sozinho, quando e em quais empresas.
            </Text>
          </Box>
        </Flex>
        <Tabs variant="soft-rounded" colorScheme="blue" isLazy>
          <TabList>
            <Tab fontSize="13px">Simples Nacional</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <SimplesNacionalAba />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>
    </GlassPageBg>
  );
}
